import React, { useState } from 'react';
import {
  Box,
  Avatar,
  AvatarGroup,
  Collapse,
  Tabs,
  Tab,
  Typography,
  useTheme,
  Tooltip,
  Icon,
  Paper,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CustomMarkdown from '../../CustomMarkdown';
import { useColorMode } from 'src/theme/ThemeProvider';
import MessageMetadataDebug from './MessageMetadataDebug';
import MessageProgress from './MessageProgress';
import CalendarView from './CalendarView';
import { keyframes } from '@mui/system';
import MessageOptions from './MessageOptions';
import { useAppDispatch } from 'src/store';
import { deleteMessage } from 'src/features/account/messages.slice';
import { decrementMessageCount } from 'src/features/account/conversations.slice';

interface FunctionMessageGroupV2Props {
  messages: any[];
  channelId?: string;
  conversationDeliveryState?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`function-tabpanel-${index}`}
      aria-labelledby={`function-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

// Success icon pop-in animation
const successPopIn = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  60% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const FunctionMessageGroupV2: React.FC<FunctionMessageGroupV2Props> = ({
  messages,
  channelId,
  conversationDeliveryState,
}) => {
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();
  const { mode } = useColorMode();
  const isDarkMode = mode === 'dark';
  const dispatch = useAppDispatch();

  const handleAvatarClick = (messageId: string) => {
    if (selectedMessageId === messageId) {
      setSelectedMessageId(null);
    } else {
      setSelectedMessageId(messageId);
      setTabValue(0); // Reset to first tab when switching messages
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getIconForMessage = (message: any) => {
    const iconUrl = message.json_content?.icon_url;
    const iconTooltip = message.json_content?.icon_tooltip || message.metadata?.function_name || 'Function';
    return { iconUrl, iconTooltip };
  };

  const extractJsonResponses = (message: any) => {
    if (message.json_content) {
      return message.json_content;
    }
    const functionResponses = message.metadata?.function_responses || message.metadata?.chosen_functions_responses;
    if (functionResponses && Array.isArray(functionResponses) && functionResponses.length > 0) {
      return functionResponses;
    }
    return null;
  };

  const selectedMessage = messages.find(m => m.id === selectedMessageId);
  const hasCalendarData = selectedMessage?.metadata?.calendar_data &&
    selectedMessage?.metadata?.function_name === 'get_available_timeslots';

  const handleDeleteSelected = () => {
    if (!selectedMessage) return;
    const resolvedChannelId = channelId || selectedMessage.channel_id;
    dispatch(
      deleteMessage({
        messageId: selectedMessage.id,
        channelId: resolvedChannelId,
        conversationId: selectedMessage.conversation_id,
      })
    );
    dispatch(
      decrementMessageCount({ conversationId: selectedMessage.conversation_id })
    );
    setSelectedMessageId(null);
  };

  return (
    <Box
      sx={{
        width: '100%',
        mb: selectedMessageId ? 1 : 0, // Only add bottom margin when expanded
        pl: '4px',
        pr: { xs: '4px', sm: 0 },
        position: 'relative',
      }}
      data-message-type="function-group"
    >
      {/* Avatar Stack */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: selectedMessageId ? 1 : 0, // Only add bottom margin when expanded
          pl: 2,
        }}
      >
        <AvatarGroup max={10} spacing="medium">
          {messages.map((message) => {
            const { iconUrl, iconTooltip } = getIconForMessage(message);
            const isSelected = selectedMessageId === message.id;
            const hasError = message.metadata?.step === 'aborted' ||
              message.metadata?.failed_step ||
              message.metadata?.error_message ||
              message.metadata?.retry_aborted;
            const progress = message.metadata?.progress;
            const isComplete = progress !== undefined && progress >= 100;

            return (
              <Tooltip key={message.id} title={iconTooltip} arrow>
                <Avatar
                  onClick={() => handleAvatarClick(message.id)}
                  sx={{
                    width: 32,
                    height: 32,
                    cursor: 'pointer',
                    bgcolor: isSelected
                      ? theme.palette.primary.main
                      : hasError
                      ? alpha(theme.palette.error.main, 0.2)
                      : isComplete
                      ? alpha(theme.palette.success.main, 0.2)
                      : isDarkMode 
                      ? alpha(theme.palette.grey[700], 0.5) // Neutral gray for dark mode
                      : alpha(theme.palette.grey[300], 0.5), // Neutral gray for light mode
                    border: `2px solid ${
                      isSelected
                        ? theme.palette.primary.main
                        : hasError
                        ? theme.palette.error.main
                        : isComplete
                        ? theme.palette.success.main
                        : theme.palette.divider
                    }`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      zIndex: 10,
                    },
                  }}
                >
                  {iconUrl ? (
                    <Box
                      component="img"
                      src={iconUrl}
                      alt=""
                      sx={{ width: 20, height: 20 }}
                    />
                  ) : (
                    <Icon sx={{ fontSize: 18 }}>
                      {hasError ? 'error' : isComplete ? 'check_circle' : 'apps'}
                    </Icon>
                  )}
                </Avatar>
              </Tooltip>
            );
          })}
        </AvatarGroup>

        {/* Progress/Status indicators for all messages */}
        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
          {messages.map((message) => {
            const progress = message.metadata?.progress;
            if (progress !== undefined && progress < 100) {
              return (
                <Tooltip key={message.id} title={`${message.json_content?.icon_tooltip || 'Function'}: ${progress}%`}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: `conic-gradient(${theme.palette.primary.main} ${progress * 3.6}deg, ${alpha(theme.palette.primary.main, 0.1)} 0deg)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: '10px' }}>
                      {Math.round(progress)}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            }
            return null;
          })}
        </Box>
      </Box>

      {/* Expandable Panel */}
      <Collapse in={!!selectedMessage} timeout="auto" unmountOnExit>
        {selectedMessage && (
          <Paper
            elevation={0}
            sx={{
              ml: { xs: 2, sm: 4 },
              mr: { xs: 2, sm: 2 },
              bgcolor: isDarkMode
                ? alpha(theme.palette.background.paper, 0.6)
                : alpha(theme.palette.grey[100], 0.8),
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: 'hidden',
              // Reveal action buttons when hovering anywhere on the expanded group
              '&:hover .action-buttons': {
                opacity: 1,
              },
            }}
          >
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="function message tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Content" />
                {extractJsonResponses(selectedMessage) && <Tab label="JSON Response" />}
                {selectedMessage.metadata && <Tab label="Metadata" />}
                <Tab label="Debug" />
              </Tabs>
            </Box>

            {/* Tab Panels */}
            <TabPanel value={tabValue} index={0}>
              {/* Raw Content */}
              <Box
                sx={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.grey[500],
                    borderRadius: '3px',
                  },
                }}
              >
                <CustomMarkdown>
                  {selectedMessage.raw_content || selectedMessage.content || "No content available."}
                </CustomMarkdown>
              </Box>
            </TabPanel>

            {extractJsonResponses(selectedMessage) && (
              <TabPanel value={tabValue} index={1}>
                {/* JSON Response */}
                <Box
                  sx={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme.palette.grey[500],
                      borderRadius: '3px',
                    },
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      padding: 0,
                      fontSize: '12px',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: isDarkMode ? '#e1e4e8' : '#24292e',
                      lineHeight: 1.5,
                    }}
                  >
                    {JSON.stringify(extractJsonResponses(selectedMessage), null, 2)}
                  </pre>
                </Box>
              </TabPanel>
            )}

            {selectedMessage.metadata && (
              <TabPanel value={tabValue} index={selectedMessage.metadata ? 2 : 1}>
                {/* Metadata */}
                <Box
                  sx={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme.palette.grey[500],
                      borderRadius: '3px',
                    },
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      padding: 0,
                      fontSize: '12px',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: isDarkMode ? '#e1e4e8' : '#24292e',
                      lineHeight: 1.5,
                    }}
                  >
                    {JSON.stringify(selectedMessage.metadata, null, 2)}
                  </pre>
                </Box>
              </TabPanel>
            )}

            <TabPanel value={tabValue} index={extractJsonResponses(selectedMessage) && selectedMessage.metadata ? 3 : selectedMessage.metadata ? 2 : 1}>
              {/* Debug Info */}
              <MessageMetadataDebug message={selectedMessage} />
            </TabPanel>

            {/* Calendar View if applicable */}
            {hasCalendarData && (
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                <CalendarView
                  calendarData={selectedMessage.metadata.calendar_data}
                  conversationId={selectedMessage.conversation_id}
                  channelId={channelId}
                  messageId={selectedMessage.id}
                />
              </Box>
            )}

            {/* Actions */}
            <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
              <MessageOptions
                message={selectedMessage}
                onDelete={handleDeleteSelected}
                showEdit={false}
                showSend={false}
                channelId={channelId}
                conversationDeliveryState={conversationDeliveryState}
              />
            </Box>
          </Paper>
        )}
      </Collapse>
    </Box>
  );
};

export default React.memo(FunctionMessageGroupV2);
